import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { customersApi, type Customer } from '../api/customers'
import {
  PROJECT_KINDS, PROJECT_STATUSES, dayOf, endFrom, parseAmount as num, projectsApi, statusOf,
  type ProjectDetail, type ProjectKind, type ProjectRequest, type ProjectStatus,
} from '../api/projects'
import {
  Button, Callout, Card, FormGrid, LoadingState, Page, PageHeader, Select, StatusPill, TextField, useConfirm, useSnackbar,
} from '../components/ui'

/**
 * ADR-019 (Equal Point, card 3): scheda di un progetto Studio, anche nuovo (`/projects/new?customerId=`).
 * Le condizioni economiche sono la copia di quelle del contratto: per un progetto importato da Contratti
 * l'originale resta là (`sourceRef`).
 */
interface Form {
  customerId: number | null
  title: string
  kind: ProjectKind
  status: ProjectStatus
  setupAmount: string
  monthlyAmount: string
  percentage: string
  durationMonths: string
  startDate: string
  endDate: string
  notes: string
}

const EMPTY: Form = {
  customerId: null, title: '', kind: 'progetto', status: 'attivo', setupAmount: '', monthlyAmount: '', percentage: '',
  durationMonths: '', startDate: '', endDate: '', notes: '',
}

const str = (n: number | null) => (n === null ? '' : String(n).replace('.', ','))

/** Come il server: ragione sociale, altrimenti nome e cognome (vale anche per condomini e professionisti). */
function customerLabel(c: Customer): string {
  return c.companyName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.customerCode
}

function formOf(p: ProjectDetail): Form {
  return {
    customerId: p.customerId, title: p.title, kind: p.kind, status: p.status,
    setupAmount: str(p.setupAmount), monthlyAmount: str(p.monthlyAmount), percentage: str(p.percentage),
    durationMonths: p.durationMonths === null ? '' : String(p.durationMonths),
    startDate: p.startDate ?? '', endDate: p.endDate ?? '', notes: p.notes ?? '',
  }
}

export function ProjectDetailPage({ canDelete = false }: { canDelete?: boolean }) {
  const { id } = useParams()
  const isNew = !id || id === 'new'
  const projectId = isNew || !/^\d+$/.test(id) ? null : Number(id)
  const badId = !isNew && projectId === null
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const askConfirm = useConfirm()

  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [form, setForm] = useState<Form | null>(isNew ? { ...EMPTY, customerId: Number(params.get('customerId')) || null } : null)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerHits, setCustomerHits] = useState<Customer[]>([])
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (projectId === null) return
    let alive = true
    projectsApi.get(projectId).then(p => {
      if (!alive) return
      setProject(p)
      setForm(formOf(p))
    }).catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Progetto non trovato.') })
    return () => { alive = false }
  }, [projectId])

  const customerId = form?.customerId ?? null
  useEffect(() => {
    if (customerId === null) { setCustomer(null); return }
    let alive = true
    customersApi.get(customerId).then(c => { if (alive) setCustomer(c) }).catch(() => { if (alive) setCustomer(null) })
    return () => { alive = false }
  }, [customerId])

  useEffect(() => {
    const q = customerSearch.trim()
    if (q.length < 2) { setCustomerHits([]); return }
    const t = setTimeout(() => {
      customersApi.list({ search: q, page: 1, size: 10 }).then(r => setCustomerHits(r.items)).catch(() => setCustomerHits([]))
    }, 300)
    return () => clearTimeout(t)
  }, [customerSearch])

  if (badId) return <Page><PageHeader title="Progetto" icon="design_services" /><Callout tone="error">Progetto non trovato.</Callout></Page>
  if (error && !form) return <Page><PageHeader title="Progetto" icon="design_services" /><Callout tone="error">{error}</Callout></Page>
  if (!form) return <Page><LoadingState /></Page>

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm(f => (f ? { ...f, [k]: v } : f))
  // Inizio e durata: se la fine era quella calcolata, si svuota e il server la ricalcola; una fine scritta a mano resta.
  const setPeriod = (k: 'startDate' | 'durationMonths', v: string) => setForm(f => {
    if (!f) return f
    const wasAuto = f.endDate !== '' && f.endDate === endFrom(f.startDate, f.durationMonths)
    return { ...f, [k]: v, endDate: wasAuto ? '' : f.endDate }
  })

  async function save() {
    if (!form) return
    setError('')
    if (form.customerId === null) { setError('Scegli il cliente.'); return }
    const amounts = { setupAmount: num(form.setupAmount), monthlyAmount: num(form.monthlyAmount), percentage: num(form.percentage) }
    if (Object.values(amounts).some(v => v !== null && Number.isNaN(v))) { setError('Importi e percentuale devono essere numeri.'); return }
    const duration = form.durationMonths.trim() ? Number(form.durationMonths) : null
    if (duration !== null && !Number.isInteger(duration)) { setError('La durata è un numero intero di mesi.'); return }
    const body: ProjectRequest = {
      customerId: form.customerId, title: form.title, kind: form.kind, status: form.status, ...amounts,
      durationMonths: duration, startDate: form.startDate || null, endDate: form.endDate || null, notes: form.notes || null,
    }
    setSaving(true)
    try {
      if (projectId === null) {
        const r = await projectsApi.create(body)
        snackbar.show({ message: 'Progetto creato' })
        navigate(`/projects/${r.id}`, { replace: true })
      } else {
        await projectsApi.update(projectId, body)
        snackbar.show({ message: 'Progetto salvato' })
        try {
          const fresh = await projectsApi.get(projectId)
          setProject(fresh)
          setForm(formOf(fresh))
        } catch { /* salvato: la scheda si riallinea alla prossima apertura */ }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    if (projectId === null) return
    if (!await askConfirm({ title: 'Eliminare il progetto?', message: 'Il progetto sparisce dal CRM; il contratto d’origine resta in Contratti.', confirmLabel: 'Elimina', destructive: true })) return
    try {
      await projectsApi.remove(projectId)
      snackbar.show({ message: 'Progetto eliminato' })
      navigate('/projects', { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eliminazione non riuscita.')
    }
  }

  const st = statusOf(form.status)
  return (
    <Page>
      <PageHeader
        title={isNew ? 'Nuovo progetto' : form.title || 'Progetto'}
        icon="design_services"
        actions={
          <>
            {!isNew && canDelete && <Button variant="text" icon="delete" onClick={() => void remove()}>Elimina</Button>}
            <Button variant="filled" icon="save" disabled={saving} onClick={() => void save()}>{saving ? 'Salvataggio...' : 'Salva'}</Button>
          </>
        }
      />
      <div className="d-flex flex-column gap-3">
        {error && <Callout tone="error">{error}</Callout>}
        {project && (
          <Callout tone="info" role="note">
            <StatusPill tone={st.tone}>{st.label}</StatusPill>{' '}
            {project.source === 'contratti'
              ? <>Importato da Contratti (contratto {project.sourceRef}): le condizioni valide restano quelle del contratto.</>
              : <>Inserito a mano il {dayOf(project.createdAt)}.</>}
          </Callout>
        )}
        <Card variant="outlined">
          <FormGrid columns={2}>
            <div className="md-span-full">
              {form.customerId !== null ? (
                <div className="d-flex align-items-center gap-2">
                  <span>Cliente: <Link to={`/customers/${form.customerId}`}>{customer ? customerLabel(customer) : `n. ${form.customerId}`}</Link></span>
                  <Button variant="text" onClick={() => set('customerId', null)}>Cambia</Button>
                </div>
              ) : (
                <>
                  <TextField label="Cerca cliente *" placeholder="Nome, ragione sociale o codice" value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)} />
                  <div className="visually-hidden" aria-live="polite">
                    {customerSearch.trim().length >= 2 ? `${customerHits.length} clienti trovati` : ''}
                  </div>
                  {customerHits.length > 0 && (
                    <div className="d-flex flex-column mt-1" role="group" aria-label="Clienti trovati">
                      {customerHits.map(c => (
                        <Button key={c.id} variant="text" onClick={() => { set('customerId', c.id); setCustomerSearch(''); setCustomerHits([]) }}>
                          {customerLabel(c)} ({c.customerCode})
                        </Button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <TextField className="md-span-full" label="Titolo *" maxLength={200} value={form.title} onChange={e => set('title', e.target.value)} />
            <Select label="Tipo" value={form.kind} onChange={e => set('kind', e.target.value as ProjectKind)}>
              {PROJECT_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
            </Select>
            <Select label="Stato" value={form.status} onChange={e => set('status', e.target.value as ProjectStatus)}>
              {PROJECT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
            <TextField label="Importo una tantum (€)" inputMode="decimal" placeholder="1.300,00" value={form.setupAmount}
              onChange={e => set('setupAmount', e.target.value)} />
            <TextField label="Canone mensile (€)" inputMode="decimal" placeholder="49,00" value={form.monthlyAmount}
              onChange={e => set('monthlyAmount', e.target.value)} />
            <TextField label="Percentuale (%)" inputMode="decimal" placeholder="50" value={form.percentage}
              onChange={e => set('percentage', e.target.value)} />
            <TextField label="Durata (mesi)" inputMode="numeric" placeholder="12" value={form.durationMonths}
              onChange={e => setPeriod('durationMonths', e.target.value)} />
            <TextField label="Inizio" type="date" value={form.startDate} onChange={e => setPeriod('startDate', e.target.value)} />
            <TextField label="Fine" type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)}
              supportingText="Vuota: si calcola da inizio e durata." />
            <TextField className="md-span-full" label="Note" maxLength={2000} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </FormGrid>
        </Card>
      </div>
    </Page>
  )
}
